import React, { useContext, useEffect, useState } from "react";
import styles from "./mortgage-view.module.scss";
import { AttachPropertyRequest } from "../../types/mortgage";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import InputWithHeader from "../../components/Input/InputWithHeader";
import { useGooglePlacesApi } from "../../hooks/useGooglePlaces";
import { PlaceSearchItem } from "../../types/cities";
import { useDebounce } from "use-debounce";
import PlacesPopUp from "../../components/Modal/places-modal";
import { useController } from "react-hook-form";
import { useHistory } from "react-router";
import { mortgageAttachPropertyApi } from "../../swr/mortgage";
import { useQuery } from "../../hooks/useQuery";
import ToastContext from "../../contexts/ToastContext";
import LoadingContext from "../../contexts/LoadingContext";

type ExternalProperty = AttachPropertyRequest["externalProperty"];

export const externalPropertySchema: yup.ObjectSchema<ExternalProperty> =
  yup.object({
    propertyName: yup.string().required("Property name is required"),

    address: yup.string().required("Address is required"),

    propertyType: yup.string().required("Property type is required"),

    propertyValue: yup
      .number()
      .typeError("Property value must be a number")
      .moreThan(0, "Property value must be greater than 0")
      .required("Property value is required"),

    sellerName: yup.string().required("Seller name is required"),

    sellerPhone: yup
      .string()
      .required("Seller phone is required")
      .matches(/^[0-9+\s\-()]{7,15}$/, "Enter a valid phone number"),

    sellerEmail: yup
      .string()
      .required("Seller email is required")
      .email("Enter a valid email address"),
  });

const MortgageProperty = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ExternalProperty>({
    resolver: yupResolver(externalPropertySchema),
    defaultValues: {
      propertyName: "",
      address: "",
      propertyType: "",
      propertyValue: 0,
      sellerName: "",
      sellerPhone: "",
      sellerEmail: "",
    },
  });

  let query = useQuery();
  const history = useHistory();

  const { openError } = useContext(ToastContext);
  const { setLoading } = useContext(LoadingContext);

  const [addressInput, setAddressInput] = useState("");
  const [showPlaces, setShowPlaces] = useState(false);
  const [debouncedAddressInput] = useDebounce(addressInput, 400); // your existing hook

  const [center, setCenter] = useState({
    latitude: 0,
    longitude: 0,
  });

  const { data } = useGooglePlacesApi({
    input: debouncedAddressInput,
    location: `${center?.latitude},${center?.longitude}`,
    key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const {
    field: addressField,
    fieldState: { error: addressError },
  } = useController({
    name: "address",
    control,
  });

  const places: PlaceSearchItem[] = data?.results ?? [];

  const onSubmit = async (req: ExternalProperty) => {
    console.log("External property data:", data);
    // call your API here
    setLoading(true);
    try {
      let appId = query.get("dippa");
      const { data, error } = await mortgageAttachPropertyApi({
        mortgageApplicationId: appId ? Number(appId) : 0,
        externalProperty: req,
      });

      if (data) {
        history.push(`/external-doc?dippa=${appId}`);
      }

      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred"
        );
      }
    } catch (error) {
      openError("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCenter({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        console.log("Location permission denied or unavailable", error);
        // fallback stays automatically
      }
    );
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.container}>
          <span className={styles.instruction}>
            Please fill the form below to begin your mortgage application
            process
          </span>

          <div className={styles.flexBet}>
            {/* ── Property Location ── */}

            <div className={styles.input}>
              <InputWithHeader
                header="Property Location"
                placeholder="Enter Property Location"
                type="string"
                value={addressInput}
                formKey="address"
                handleChange={(_, val) => {
                  setAddressInput(val);
                  setShowPlaces(true);
                  addressField.onChange(val);
                }}
                error={addressError?.message}
              />
            </div>

            {/* ── Property Type ── */}
            <div className={styles.input}>
              <Controller
                name="propertyType"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Property Type"
                    placeholder="Enter Property Type"
                    type="string"
                    value={field.value}
                    formKey="propertyType"
                    handleChange={(_, val) => field.onChange(val)}
                    error={errors.propertyType?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className={styles.flexBet}>
            {/* ── Estimated Value ── */}
            <div className={styles.input}>
              <Controller
                name="propertyValue"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Estimated Value of Property"
                    placeholder="Enter Estimated Value"
                    type="amount"
                    value={field.value || 0}
                    formKey="propertyValue"
                    handleChange={(_, val) => field.onChange(Number(val))}
                    error={errors.propertyValue?.message}
                  />
                )}
              />
            </div>

            {/* ── Seller Name ── */}
            <div className={styles.input}>
              <Controller
                name="sellerName"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Name of Property Seller"
                    placeholder="Enter Name"
                    type="string"
                    value={field.value}
                    formKey="sellerName"
                    handleChange={(_, val) => field.onChange(val)}
                    error={errors.sellerName?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className={styles.flexBet}>
            {/* ── Seller Phone ── */}
            <div className={styles.input}>
              <Controller
                name="sellerPhone"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Phone Number of Property Seller"
                    placeholder="Enter Phone Number"
                    type="string"
                    value={field.value}
                    formKey="sellerPhone"
                    handleChange={(_, val) => field.onChange(val)}
                    error={errors.sellerPhone?.message}
                  />
                )}
              />
            </div>

            {/* ── Seller Email ── */}
            <div className={styles.input}>
              <Controller
                name="sellerEmail"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Email Address of Property Seller"
                    placeholder="Enter Email Address"
                    type="string"
                    value={field.value}
                    formKey="sellerEmail"
                    handleChange={(_, val) => field.onChange(val)}
                    error={errors.sellerEmail?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className={styles.flexBet}>
            {/* ── Seller Phone ── */}
            <div className={styles.input}>
              <Controller
                name="propertyName"
                control={control}
                render={({ field }) => (
                  <InputWithHeader
                    header="Name of Property"
                    placeholder="Enter Property Name"
                    type="string"
                    value={field.value}
                    formKey="propertyName"
                    handleChange={(_, val) => field.onChange(val)}
                    error={errors.propertyName?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className={styles["bottom-cont"]}>
            <div className={styles["back-but"]} role="button">
              <span></span>
            </div>
            <button type="submit" className={styles["submit-btn"]}>
              Save and Continue
            </button>
          </div>
        </div>
      </form>

      {showPlaces && places.length > 0 && (
        <div className={styles["places-drop"]}>
          <PlacesPopUp
            places={places}
            onSelect={(place) => {
              addressField.onChange(place.formatted_address);
              setAddressInput(place.formatted_address); // sync display
              setShowPlaces(false); // close list
            }}
          />
        </div>
      )}
    </>
  );
};

export default MortgageProperty;
